import React from 'react';
import styled from '@emotion/styled';
import { Skeleton, Tag, Typography, theme } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';

export type PermissionsCardItem = {
  key: string;
  label: string;
  /** When set, the tag renders as a link (opens in a new tab). */
  href?: string;
};

type PermissionsCardProps = {
  /** Whether to render the office administration / groups / roles sections. */
  showOfficeAdmin: boolean;
  /** Whether to render the office access (passes) section. */
  showPasses: boolean;
  /** Locations the user administers (or the org name for org-wide admins). */
  officeAdministration: PermissionsCardItem[];
  passes: PermissionsCardItem[];
  groups: PermissionsCardItem[];
  roles: PermissionsCardItem[];
  /** Office access passes are resolved separately (GraphQL). */
  passesLoading?: boolean;
};

export const PermissionsCard = ({
  showOfficeAdmin,
  showPasses,
  officeAdministration,
  passes,
  groups,
  roles,
  passesLoading = false,
}: PermissionsCardProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'permissions',
  });
  const { token } = theme.useToken();

  return (
    <Card $borderColor={token.colorBorderSecondary}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t('title')}
      </Typography.Title>

      {showOfficeAdmin && (
        <Section title={t('manages')} items={officeAdministration} />
      )}

      {showPasses && (
        <Section title={t('passes')} items={passes} loading={passesLoading} />
      )}

      {showOfficeAdmin && (
        <>
          <Section title={t('groups')} items={groups} />
          <Section title={t('roles')} items={roles} />
        </>
      )}
    </Card>
  );
};

type SectionProps = {
  title: string;
  items: PermissionsCardItem[];
  loading?: boolean;
};

const Section = ({
  title,
  items,
  loading = false,
}: SectionProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'permissions',
  });

  return (
    <SectionWrapper>
      <Typography.Text strong size="small">
        {title}
      </Typography.Text>
      {loading ? (
        <Skeleton.Button active size="small" style={{ width: 96 }} />
      ) : items.length > 0 ? (
        <TagList>
          {items.map((item) => (
            <Tag key={item.key} style={{ margin: 0 }}>
              {item.href ? (
                <a href={item.href} target="_blank" rel="noopener noreferrer">
                  {item.label}
                </a>
              ) : (
                item.label
              )}
            </Tag>
          ))}
        </TagList>
      ) : (
        <Typography.Text type="secondary">{t('empty')}</Typography.Text>
      )}
    </SectionWrapper>
  );
};

export const PermissionsCardSkeleton = (): JSX.Element => {
  const { token } = theme.useToken();
  return (
    <Card
      $borderColor={token.colorBorderSecondary}
      data-testid="permissions-card-skeleton"
    >
      <Skeleton active paragraph={{ rows: 5 }} title={{ width: '40%' }} />
    </Card>
  );
};

const Card = styled.div<{ $borderColor: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 16px 16px;
  padding: 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
